class BlogTagCounter {
  constructor(blogHandle, options = {}) {
    this.blogHandle = blogHandle;
    this.apiVersion = options.apiVersion || '2026-04';
    this.endpoint = options.endpoint || `/api/${this.apiVersion}/graphql.json`;
    this.maxPages = options.maxPages || 20;

    this.visibleArticles = [];
    this.tagCounts = {};
    this.totalVisibleArticles = 0;
    this.loaded = false;
    this.error = null;
  }

  async load() {
    this.visibleArticles = [];
    this.tagCounts = {};
    this.totalVisibleArticles = 0;
    this.loaded = false;
    this.error = null;

    let cursor = null;
    let hasNextPage = true;
    let pageCount = 0;

    const seenCursors = new Set();

    const query = `
      query GetBlogArticles($handle: String!, $cursor: String) {
        blog(handle: $handle) {
          articles(first: 250, after: $cursor) {
            nodes {
              id
              title
              handle
              tags
              publishedAt
              onlineStoreUrl
            }
            pageInfo {
              hasNextPage
              endCursor
            }
          }
        }
      }
    `;

    while (hasNextPage && pageCount < this.maxPages) {
      try {
        pageCount += 1;

        const response = await fetch(this.endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            query,
            variables: {
              handle: this.blogHandle,
              cursor
            }
          })
        });

        if (!response.ok) {
          throw new Error(`Storefront API request failed: ${response.status}`);
        }

        const json = await response.json();

        if (json.errors) {
          throw new Error('Storefront API returned GraphQL errors');
        }

        const articles = json.data?.blog?.articles;

        if (!articles) {
          break;
        }

        articles.nodes.forEach((article) => {
          if (!this.isVisibleArticle(article)) return;

          this.visibleArticles.push(article);
          this.totalVisibleArticles += 1;

          const uniqueTags = new Set(article.tags || []);

          uniqueTags.forEach((tag) => {
            this.tagCounts[tag] = (this.tagCounts[tag] || 0) + 1;
          });
        });

        const nextCursor = articles.pageInfo.endCursor;

        hasNextPage = Boolean(articles.pageInfo.hasNextPage);

        // Prevent infinite request if Shopify returns hasNextPage true
        // but does not provide a new cursor.
        if (hasNextPage && !nextCursor) {
          console.warn('Stopping article fetch: missing next cursor.');
          break;
        }

        // Prevent infinite request if the same cursor repeats.
        if (nextCursor && seenCursors.has(nextCursor)) {
          console.warn('Stopping article fetch: repeated cursor detected.');
          break;
        }

        if (nextCursor) {
          seenCursors.add(nextCursor);
        }

        cursor = nextCursor;
      } catch (error) {
        console.error(error);
        this.error = error;
        break;
      }
    }

    if (pageCount >= this.maxPages) {
      console.warn(`Stopping article fetch: maxPages limit of ${this.maxPages} reached.`);
    }

    this.loaded = true;

    return this;
  }

  isVisibleArticle(article) {
    return Boolean(article?.publishedAt && article?.onlineStoreUrl);
  }

  getArticlesCount() {
    return String(this.totalVisibleArticles);
  }

  getTagCount(tag) {
    if (!tag) return '0';

    if (this.tagCounts[tag] !== undefined) {
      return String(this.tagCounts[tag]);
    }

    const targetHandle = this.handleize(tag);

    const matchedTag = Object.keys(this.tagCounts).find((existingTag) => {
      return this.handleize(existingTag) === targetHandle;
    });

    return String(matchedTag ? this.tagCounts[matchedTag] : 0);
  }

  getVisibleArticles() {
    return this.visibleArticles;
  }

  hasError() {
    return Boolean(this.error);
  }

  handleize(value) {
    return String(value)
      .toLowerCase()
      .trim()
      .replace(/['"]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  setAllCount() {
    const allArticlesCount = document.querySelector('[data-all-articles-count]');
    if (allArticlesCount) {
      allArticlesCount.textContent = this.getArticlesCount();
      allArticlesCount.classList.remove('hdt-hidden');
    }
  }

  setTagCounts() {
    const tagCountElements = document.querySelectorAll('[data-tag-count]');
    if (tagCountElements) {
      tagCountElements.forEach(el => {
        el.textContent = this.getTagCount(el.dataset.tag);
        el.classList.remove('hdt-hidden');
      });
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  if (!!window.blogHandle) {
    const blogTagCounter = new BlogTagCounter(window.blogHandle);
    blogTagCounter.load().then(() => {
      blogTagCounter.setAllCount();
      blogTagCounter.setTagCounts();
    });
  }
});